import { createReducer, on } from '@ngrx/store';

import { PrescriptionsState } from '@hm/core/models/prescriptions-state.interface';
import {
  addPrescriptionDraft,
  clearPrescriptions,
  deletePrescription,
  deletePrescriptionDraft,
  editPrescription,
  fetchPrescriptions,
  fetchPrescriptionsFailure,
  fetchPrescriptionsSuccess,
  incrementPrescriptionsPage,
  loadPrescriptionDraftsFromStorage,
  processPrescriptionsDrafts,
  processPrescriptionsDraftsFailure,
  processPrescriptionsDraftsSuccess,
  resetPrescriptionsState,
  savePrescription,
  setPrescriptionsInitRequired,
  setPrescriptionsPage,
  setPrescriptionsSearchFilter,
  updatePrescriptionDraft,
} from '@hm/core/stores/prescriptions/prescriptions.actions';

import { generateHistoricalPrescriptionsByDate } from '../../functions/generate-historical-prescriptions-recursive.function';

const initialState: PrescriptionsState = {
  prescriptionSubmitted: false,
  prescriptionDraftSubmitted: false,
  prescriptionSaved: false,
  prescriptionEdited: null,
  prescriptionDeleted: null,

  initRequired: true,
  guaranteesFilters: {
    search: '',
  },
  count: 0,
  page: 1,
  pageSize: 10,
  hasNextPage: true,
  hasPreviousPage: false,
  guarantees: [],
  draftsProcessing: false,
  drafts: [],
  loading: false,
  guaranteesFetching: false,
};

export const prescriptionsReducer = createReducer(
  initialState,
  on(savePrescription, state => ({ ...state, prescriptionSaved: true })),
  on(editPrescription, (state, { editData }) => ({ ...state, prescriptionEdited: editData })),
  on(deletePrescription, (state, { deletionData }) => ({ ...state, prescriptionDeleted: deletionData })),
  on(setPrescriptionsInitRequired, (state, { initRequired }) => ({ ...state, initRequired })),
  on(setPrescriptionsSearchFilter, (state, { search }) => ({ ...state, guaranteesFilters: { ...state.guaranteesFilters, search } })),
  on(setPrescriptionsPage, (state, { page }) => ({ ...state, page })),
  on(incrementPrescriptionsPage, state => ({ ...state, page: state.page + 1, guaranteesFetching: true })),
  on(fetchPrescriptions, state => ({ ...state, loading: true, guaranteesFetching: true })),
  on(fetchPrescriptionsSuccess, (state, { pagedResponse }) => {
    const updatedGuarantees = generateHistoricalPrescriptionsByDate(pagedResponse);
    return {
      ...state,
      count: pagedResponse.count,
      page: pagedResponse.page,
      pageSize: pagedResponse.pageSize,
      hasNextPage: pagedResponse.hasNextPage,
      hasPreviousPage: pagedResponse.hasPreviousPage,
      guarantees: [...state.guarantees, ...updatedGuarantees],
      loading: false,
      guaranteesFetching: false,
    };
  }),
  on(fetchPrescriptionsFailure, state => ({ ...state, loading: false, guaranteesFetching: false })),
  on(loadPrescriptionDraftsFromStorage, (state, { drafts }) => ({
    ...state,
    drafts,
  })),
  on(addPrescriptionDraft, (state, { draft }) => ({ ...state, prescriptionDraftSubmitted: false, drafts: [...state.drafts, draft] })),
  on(updatePrescriptionDraft, (state, { draft }) => {
    const existingDraftIndex = state.drafts.findIndex(existingDraft => existingDraft.id === draft.id);
    if (existingDraftIndex !== -1) {
      const updatedDrafts = state.drafts.map(existingDraft => (existingDraft.id === draft.id ? { ...existingDraft, ...draft } : existingDraft));
      return { ...state, prescriptionDraftSubmitted: false, drafts: updatedDrafts };
    } else {
      return { ...state, prescriptionDraftSubmitted: false, drafts: [...state.drafts, draft] };
    }
  }),
  on(deletePrescriptionDraft, (state, { id }) => ({
    ...state,
    drafts: state.drafts.filter(draft => draft.id !== id),
  })),
  on(processPrescriptionsDrafts, state => ({
    ...state,
    loading: true,
    draftsProcessing: true,
  })),
  on(processPrescriptionsDraftsSuccess, state => ({
    ...state,
    loading: false,
    draftsProcessing: false,
    drafts: [],
  })),
  on(processPrescriptionsDraftsFailure, state => ({
    ...state,
    loading: false,
    draftsProcessing: false,
  })),
  on(clearPrescriptions, state => ({
    ...state,
    count: initialState.count,
    page: initialState.page,
    pageSize: initialState.pageSize,
    hasNextPage: initialState.hasNextPage,
    hasPreviousPage: initialState.hasPreviousPage,
    guarantees: initialState.guarantees,
    loading: initialState.loading,
    guaranteesFetching: initialState.guaranteesFetching,
  })),
  on(resetPrescriptionsState, () => initialState)
);
